import { Form, SelectProps, Spin } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";

import { _isRequired, parseErrorMessage, renderUserListOptions } from "utils";
import { USER_STATUS_ENUM, UserListEntity } from "interfaces";
import { getUserList } from "services";
import { SearchSelect } from "ui";
import { debounce } from "lodash";
import { NormalizedError } from "services/api/errorHandler";

interface IProps {
  inputProps: SelectProps;
  name: string | string[];
}

export const UserSelect = (props: IProps) => {
  const [users, setUsers] = useState<UserListEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [open, setOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const isSearchingRef = useRef(false);
  const forceKeepOpenRef = useRef(false);

  const preventDropdownClose = useCallback(() => {
    setTimeout(() => {
      setOpen(true);
    }, 0);
  }, []);

  const performSearch = useCallback(
    (value: string) => {
      const fetchData = async () => {
        forceKeepOpenRef.current = true;
        setLoading(true);
        setUsers([]);
        setHasSearched(true);
        isSearchingRef.current = true;
        preventDropdownClose();

        try {
          const res = await getUserList({
            size: 10000,
            page: 0,
            sort: ["surname,asc"],
            searchTerm: value,
            statuses: [USER_STATUS_ENUM.ACTIVE, USER_STATUS_ENUM.DRAFT],
          });
          if (res.content && res.content.length > 0) {
            setUsers(res.content ?? []);
          }
          preventDropdownClose();
        } catch (e: unknown) {
          console.error(parseErrorMessage(e as NormalizedError));
        } finally {
          setLoading(false);
          preventDropdownClose();

          setTimeout(() => {
            isSearchingRef.current = false;
            forceKeepOpenRef.current = false;
          }, 300);
        }
      };

      fetchData();
    },
    [preventDropdownClose]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value.length >= 3) {
        performSearch(value);
      }
    }, 500),
    [performSearch]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        if (searchValue.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          forceKeepOpenRef.current = true;
          debouncedSearch.cancel();
          performSearch(searchValue);

          preventDropdownClose();
          return false;
        }
      }
    },
    [debouncedSearch, performSearch, preventDropdownClose, searchValue]
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleDropdownVisibleChange = useCallback(
    (visible: boolean) => {
      if (forceKeepOpenRef.current && !visible) {
        preventDropdownClose();
        return;
      }
      setOpen(visible);
    },
    [preventDropdownClose]
  );

  const handleSelect = useCallback(
    (value: any, option: any) => {
      if (isSearchingRef.current) {
        preventDropdownClose();
        return false;
      }

      if (props.inputProps.onSelect) {
        props.inputProps.onSelect(value, option);
      }
    },
    [preventDropdownClose, props.inputProps]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <Form.Item name={props.name} rules={[_isRequired("Компания обязательна для ввода")]} className="full-width">
      <SearchSelect
        {...props.inputProps}
        placeholder="Начните вводить имя или телефон"
        disabled={props.inputProps.disabled}
        loading={loading}
        optionLabelProp="label"
        onSearch={handleSearch}
        onKeyDown={handleKeyDown}
        onSelect={handleSelect}
        open={open}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        filterOption={false}
        showSearch
        defaultActiveFirstOption={false}
        notFoundContent={
          loading ? (
            <Spin size="small" />
          ) : users.length === 0 ? (
            hasSearched ? (
              "Нет данных"
            ) : (
              "Начните вводить символы для поиска (мин. 3 символа)"
            )
          ) : null
        }
      >
        {renderUserListOptions(users)}
      </SearchSelect>
    </Form.Item>
  );
};
